import { api } from '@/api';
import {
  fetchInitiateRefund,
  fetchSendTicketsToEmail,
} from '@/features/manage/manageService';
import { fetchBooking, fetchIssuer } from '@/features/purchase/purchaseService';
import { createAsyncThunk } from '@/store/utils';
import type {
  AdmissionDTO,
  BookingDTO,
  FulfillmentDTO,
  RefundOffersDTO,
  SendTicketDeliveryV2RequestDTO,
} from '@/types/dto/agent';
import type { TravelAccountDTO, TravelAccountsMap } from '@/types/travelpass';
import { runInSequence } from '@/utils/runInSequence';
import { isNonEmptyString } from '@/utils/typeGuards';
import { createAction } from '@reduxjs/toolkit';

export const resetRefundSelection = createAction('manage/resetRefundSelection');

export const setChangeTripsSelectedTripId = createAction<{
  tripId: string | null;
  isOutbound: boolean;
}>('manage/setChangeTripsSelectedTripId');

export const resetChangeTripsSelection = createAction(
  'manage/resetChangeTripsSelection'
);

export const toggleRefundLeg = createAction(
  'manage/toggleRefundLeg',
  function prepare(
    legId: string,
    selectedLegAdmissions: Record<string, Array<AdmissionDTO>>,
    admissions: Array<AdmissionDTO> = []
  ) {
    const legAdmissions = selectedLegAdmissions[legId] || [];
    const areAllSelected = legAdmissions.length === admissions?.length;

    return {
      payload: {
        legId,
        admissions: areAllSelected ? [] : admissions,
      },
    };
  }
);

export const toggleRefundLegAdmission = createAction(
  'manage/toggleRefundLegAdmission',
  function prepare(
    legId: string,
    selectedLegAdmissions: Record<string, Array<AdmissionDTO>>,
    admission: AdmissionDTO
  ) {
    const legAdmissions = selectedLegAdmissions[legId] || [];
    const updatedLegAdmissions = legAdmissions.some(
      (a) => a.id === admission?.id
    )
      ? legAdmissions.filter((a) => a.id !== admission?.id)
      : [...legAdmissions, admission];

    return {
      payload: {
        legId,
        admissions: updatedLegAdmissions,
      },
    };
  }
);

export const toggleRefundNonTripAdmission = createAction<AdmissionDTO>(
  'purchase/toggleRefundNonTripAdmission'
);

export const initiateLegsRefund = createAsyncThunk<
  RefundOffersDTO | null,
  void
>('manage/initiateLegsRefund', async (_, { getState }) => {
  const state = getState();
  const { selectedLegAdmissions } = state.manage.refund;
  const bookingId = state.manage.booking?.id;

  const fulfillmentIds = Object.values(selectedLegAdmissions)
    .flat()
    .filter((admission) => admission.status !== 'REFUNDED')
    .flatMap((admission) => [
      ...(admission.fulfillments ?? []).map((f) => f.id),
      ...(admission.ancillaries?.flatMap(
        (ancillary) => ancillary.fulfillments?.map((f) => f.id) ?? []
      ) ?? []),
    ])
    .filter(isNonEmptyString);

  return fetchInitiateRefund(bookingId, fulfillmentIds);
});

export const initiateNonTripOffersRefund = createAsyncThunk<
  RefundOffersDTO | null,
  void
>('manage/initiateNonTripOffersRefund', async (_, { getState }) => {
  const state = getState();
  const { selectedNonTripAdmissions } = state.manage.refund;
  const bookingId = state.manage.booking?.id;

  if (!bookingId) {
    return null;
  }

  const fulfillmentIds = Object.keys(selectedNonTripAdmissions);

  return fetchInitiateRefund(bookingId, fulfillmentIds);
});

export const deleteRefundOffer = createAsyncThunk<void, void>(
  'manage/deleteRefundOffer',
  async (_, { getState }) => {
    const state = getState();
    const bookingId = state.manage.booking?.id;

    if (!bookingId) {
      return;
    }

    await runInSequence(
      (state.manage.refund.details?.refundOffers ?? []).map(
        (offer) => () =>
          offer.id &&
          api.agentApi.RefundOffers_DeleteRefund({
            bookingId,
            refundOfferId: offer.id,
          })
      )
    );
  }
);

export const confirmRefundOffer = createAsyncThunk<void, void>(
  'manage/confirmRefundOffer',
  async (_, { getState }) => {
    const state = getState();
    const bookingId = state.manage.booking?.id;

    if (!bookingId) {
      return;
    }

    await runInSequence(
      (state.manage.refund.details?.refundOffers ?? []).map(
        (offer) => () =>
          offer.id &&
          api.agentApi.RefundOffers_ConfirmRefund(
            { bookingId, refundOfferId: offer.id },
            { status: 'CONFIRMED' }
          )
      )
    );
  }
);

export const getManageFlowBookingById = createAsyncThunk<
  BookingDTO | null,
  string | null | undefined
>('manage/getManageFlowBookingById', async (id) => {
  if (!id) {
    return null;
  }

  return fetchBooking(id);
});

export const getManageFlowBookingByNumberAndEmail = createAsyncThunk<
  BookingDTO | null,
  {
    bookingNumber: string;
    email: string;
  }
>(
  'manage/getManageFlowBookingByNumberAndEmail',
  async ({ bookingNumber, email }, { dispatch }) => {
    const { bookingsSearchResults } = (
      await api.proxyApi.post<{
        bookingsSearchResults?: Array<{ id: string }>;
      }>('/bookings-search/generic', {
        reference: bookingNumber,
        purchaserPassengerEmail: email,
      })
    ).data;

    if (bookingsSearchResults?.length === 1) {
      return await dispatch(
        getManageFlowBookingById(bookingsSearchResults[0].id)
      ).unwrap();
    }

    return null;
  }
);

export const clearManageFlowBooking = createAction(
  'manage/clearManageFlowBooking'
);

export const getManageFlowTravelAccountsMap = createAsyncThunk<
  TravelAccountsMap,
  Array<FulfillmentDTO>
>('manage/getManageFlowTravelAccountsMap', async (fulfillments) => {
  const isValidFulfillment = (
    fulfillment: FulfillmentDTO
  ): fulfillment is { controlNumber: string; issuer: string } =>
    Boolean(fulfillment.controlNumber) && Boolean(fulfillment.issuer);

  const travelAccounts = await Promise.all(
    fulfillments.filter(isValidFulfillment).map((fulfillment) =>
      fetchIssuer({
        controlNumber: fulfillment.controlNumber,
        issuer: fulfillment.issuer,
      })
    )
  );

  return travelAccounts.reduce<Record<string, TravelAccountDTO>>(
    (acc, { travelAccount }) => {
      if (travelAccount?.issuer && travelAccount.number) {
        acc[`${travelAccount.issuer}-${travelAccount.number}`] = travelAccount;
      }

      return acc;
    },
    {}
  );
});

export const sendTicketsToEmail = createAsyncThunk<
  void,
  SendTicketDeliveryV2RequestDTO
>('manage/sendTicketsToEmail', async (request) => {
  await fetchSendTicketsToEmail(request);
});
